import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store';
import { logout } from '../store/slices/authSlice';
import { clientAuthApi } from '../services/clientAuthApi';
import { AlertTriangle, Trash2, Download, CheckCircle, Loader2, X } from 'lucide-react';
import AccountLayout from '../components/AccountLayout';
import { ModalHeader } from '../components/ModalHeader';

export default function DeleteAccountPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [password, setPassword] = useState('');
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [step, setStep] = useState<'warning' | 'confirm' | 'reason'>('warning');

  const reasons = [
    'notUseful',
    'tooExpensive',
    'foundAlternative',
    'privacyConcerns',
    'technicalIssues',
    'other',
  ];

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setStep('warning');
    setConfirmText('');
    setPassword('');
    setReason('');
    setErrorMessage('');
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    setErrorMessage('');

    try {
      // Validate inputs
      if (confirmText !== 'DELETE') {
        setErrorMessage(t('auth.account.deleteAccount.modal.errors.confirmTextMismatch'));
        setLoading(false);
        return;
      }

      if (!password) {
        setErrorMessage(t('auth.account.deleteAccount.modal.errors.passwordRequired'));
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('client_token');
      if (!token) {
        setErrorMessage(t('auth.account.deleteAccount.modal.errors.authRequired'));
        return;
      }

      await clientAuthApi.deleteAccount(token, { password, reason: reason || undefined });

      // Logout user
      dispatch(logout());
      
      // Redirect to home with success message
      navigate('/', { 
        state: { 
          message: t('auth.account.deleteAccount.success.message') 
        } 
      });
    } catch (error) {
      console.error('Failed to delete account:', error);
      const code = (error as Error & { code?: string }).code;
      setErrorMessage(
        code === 'INVALID_PASSWORD'
          ? t('auth.account.deleteAccount.modal.errors.passwordIncorrect')
          : t('auth.account.deleteAccount.modal.errors.deleteFailed')
      );
    } finally {
      setLoading(false);
    }
  };

  const handleExportData = () => {
    navigate('/account/export-data');
  };

  return (
    <AccountLayout>
      <div className="w-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-display font-bold text-textPrimary mb-2">
            {t('auth.account.deleteAccount.title')}
          </h1>
          <p className="text-textSecondary">
            {t('auth.account.deleteAccount.subtitle')}
          </p>
        </div>

        {/* Warning Section */}
        <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-start space-x-3 mb-4">
            <AlertTriangle className="w-8 h-8 text-red-600 flex-shrink-0" />
            <div>
              <h2 className="text-xl font-bold text-red-900 mb-2">
                {t('auth.account.deleteAccount.warning.title')}
              </h2>
              <p className="text-red-800 mb-4">
                {t('auth.account.deleteAccount.warning.description')}
              </p>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4">
            <h3 className="font-semibold text-textPrimary mb-3">
              {t('auth.account.deleteAccount.warning.consequences')}
            </h3>
            <ul className="space-y-2 text-sm">
              <li className="flex items-start">
                <X className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-textSecondary">
                  {t('auth.account.deleteAccount.warning.consequence1')}
                </span>
              </li>
              <li className="flex items-start">
                <X className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-textSecondary">
                  {t('auth.account.deleteAccount.warning.consequence2')}
                </span>
              </li>
              <li className="flex items-start">
                <X className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-textSecondary">
                  {t('auth.account.deleteAccount.warning.consequence3')}
                </span>
              </li>
              <li className="flex items-start">
                <X className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-textSecondary">
                  {t('auth.account.deleteAccount.warning.consequence4')}
                </span>
              </li>
              <li className="flex items-start">
                <X className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                <span className="text-textSecondary">
                  {t('auth.account.deleteAccount.warning.consequence5')}
                </span>
              </li>
            </ul>
          </div>
        </div>

        {/* Export Data Reminder */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="flex items-start justify-between">
            <div className="flex items-start space-x-3 flex-1">
              <Download className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-textPrimary mb-2">
                  {t('auth.account.deleteAccount.exportReminder.title')}
                </h3>
                <p className="text-sm text-textSecondary mb-4">
                  {t('auth.account.deleteAccount.exportReminder.description')}
                </p>
                <button
                  onClick={handleExportData}
                  className="btn-secondary flex items-center space-x-2"
                >
                  <Download className="w-4 h-4" />
                  <span>{t('auth.account.deleteAccount.exportReminder.button')}</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Alternatives Section */}
        <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 mb-6">
          <h2 className="text-xl font-semibold text-textPrimary mb-4">
            {t('auth.account.deleteAccount.alternatives.title')}
          </h2>
          <p className="text-sm text-textSecondary mb-4">
            {t('auth.account.deleteAccount.alternatives.description')}
          </p>

          <div className="space-y-3">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-textPrimary mb-1">
                {t('auth.account.deleteAccount.alternatives.deactivate.title')}
              </h4>
              <p className="text-sm text-textSecondary">
                {t('auth.account.deleteAccount.alternatives.deactivate.description')}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-textPrimary mb-1">
                {t('auth.account.deleteAccount.alternatives.privacy.title')}
              </h4>
              <p className="text-sm text-textSecondary">
                {t('auth.account.deleteAccount.alternatives.privacy.description')}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <h4 className="font-medium text-textPrimary mb-1">
                {t('auth.account.deleteAccount.alternatives.support.title')}
              </h4>
              <p className="text-sm text-textSecondary">
                {t('auth.account.deleteAccount.alternatives.support.description')}
              </p>
            </div>
          </div>
        </div>

        {/* Delete Button */}
        <div className="bg-white rounded-xl shadow-sm p-6 border-2 border-red-200">
          <h2 className="text-xl font-semibold text-red-900 mb-4">
            {t('auth.account.deleteAccount.dangerZone.title')}
          </h2>
          <p className="text-sm text-textSecondary mb-6">
            {t('auth.account.deleteAccount.dangerZone.description')}
          </p>

          <button
            onClick={() => setShowDeleteModal(true)}
            className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center space-x-2"
          >
            <Trash2 className="w-5 h-5" />
            <span>{t('auth.account.deleteAccount.dangerZone.button')}</span>
          </button>
        </div>

        {/* Delete Confirmation Modal */}
        {showDeleteModal && createPortal(
          <div
            role="dialog"
            aria-modal="true"
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '1rem',
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
            }}
          >
            <div className="modal-panel rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <ModalHeader
                  title={
                    <div className="flex items-center space-x-3 pr-2">
                      <div className="flex items-center justify-center w-12 h-12 bg-red-100 rounded-full shrink-0">
                        <AlertTriangle className="w-6 h-6 text-red-600" />
                      </div>
                      <h3 className="text-2xl font-bold text-textPrimary">
                        {step === 'warning' && t('auth.account.deleteAccount.modal.warningTitle')}
                        {step === 'reason' && t('auth.account.deleteAccount.modal.reasonTitle')}
                        {step === 'confirm' && t('auth.account.deleteAccount.modal.confirmTitle')}
                      </h3>
                    </div>
                  }
                  onClose={closeDeleteModal}
                  disabled={loading}
                  className="mb-6"
                />

                {errorMessage && (
                  <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2">
                    <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
                    <p className="text-sm text-red-800">{errorMessage}</p>
                  </div>
                )}

                {/* Step 1: Warning */}
                {step === 'warning' && (
                  <div className="space-y-4">
                    <p className="text-textSecondary">
                      {t('auth.account.deleteAccount.modal.warningDescription')}
                    </p>

                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <p className="text-sm font-medium text-red-900 mb-2">
                        {t('auth.account.deleteAccount.modal.warningList')}
                      </p>
                      <ul className="space-y-1 text-sm text-red-800">
                        <li>• {t('auth.account.deleteAccount.modal.warning1')}</li>
                        <li>• {t('auth.account.deleteAccount.modal.warning2')}</li>
                        <li>• {t('auth.account.deleteAccount.modal.warning3')}</li>
                      </ul>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => setStep('reason')}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex-1"
                      >
                        {t('auth.account.deleteAccount.modal.continue')}
                      </button>
                      <button
                        onClick={closeDeleteModal}
                        className="btn-secondary flex-1"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 2: Reason */}
                {step === 'reason' && (
                  <div className="space-y-4">
                    <p className="text-textSecondary">
                      {t('auth.account.deleteAccount.modal.reasonDescription')}
                    </p>

                    <div className="space-y-2">
                      {reasons.map((reasonKey) => (
                        <label
                          key={reasonKey}
                          className="flex items-center p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 transition-colors"
                        >
                          <input
                            type="radio"
                            name="reason"
                            value={reasonKey}
                            checked={reason === reasonKey}
                            onChange={(e) => setReason(e.target.value)}
                            className="mr-3"
                          />
                          <span className="text-sm text-textPrimary">
                            {t(`auth.account.deleteAccount.reasons.${reasonKey}`)}
                          </span>
                        </label>
                      ))}
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={() => setStep('confirm')}
                        disabled={!reason}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {t('auth.account.deleteAccount.modal.continue')}
                      </button>
                      <button
                        onClick={() => setStep('warning')}
                        className="btn-secondary flex-1"
                      >
                        {t('common.previous')}
                      </button>
                      <button
                        onClick={closeDeleteModal}
                        className="btn-secondary flex-1"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}

                {/* Step 3: Final Confirmation */}
                {step === 'confirm' && (
                  <div className="space-y-4">
                    <p className="text-textSecondary mb-4">
                      {t('auth.account.deleteAccount.modal.confirmDescription')}
                    </p>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('auth.account.deleteAccount.modal.typeDelete')}
                      </label>
                      <input
                        type="text"
                        value={confirmText}
                        onChange={(e) => setConfirmText(e.target.value)}
                        placeholder="DELETE"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                      <p className="mt-1 text-xs text-textSecondary">
                        {t('auth.account.deleteAccount.modal.typeDeleteHelp')}
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-textPrimary mb-2">
                        {t('auth.account.deleteAccount.modal.confirmPassword')}
                      </label>
                      <input
                        type="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder={t('auth.account.deleteAccount.modal.passwordPlaceholder')}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent"
                      />
                    </div>

                    <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                      <p className="text-sm text-yellow-900">
                        <strong>{t('auth.account.deleteAccount.modal.finalWarning')}</strong>
                      </p>
                    </div>

                    <div className="flex space-x-3">
                      <button
                        onClick={handleDeleteAccount}
                        disabled={loading || confirmText !== 'DELETE' || !password}
                        className="bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center space-x-2 flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{t('auth.account.deleteAccount.modal.deleting')}</span>
                          </>
                        ) : (
                          <>
                            <Trash2 className="w-5 h-5" />
                            <span>{t('auth.account.deleteAccount.modal.deleteButton')}</span>
                          </>
                        )}
                      </button>
                      <button
                        onClick={() => setStep('reason')}
                        disabled={loading}
                        className="btn-secondary flex-1"
                      >
                        {t('common.previous')}
                      </button>
                      <button
                        onClick={closeDeleteModal}
                        disabled={loading}
                        className="btn-secondary flex-1"
                      >
                        {t('common.cancel')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>,
          document.body
        )}
      </div>
    </AccountLayout>
  );
}

